import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/contexts/AuthContext";
import { ADMIN } from "@/constants/testIds";
import {
  Plus, Upload, Download, Search, Trash2, Edit2, Eye,
  Package, AlertCircle, X, RefreshCw, ChevronLeft, ChevronRight
} from "lucide-react";

export default function ProductList() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [importLoading, setImportLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [bulkConfirm, setBulkConfirm] = useState(null); // { ids, label }
  const [bulkTyped, setBulkTyped] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const importRef = useRef();
  const navigate = useNavigate();

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/excel/template`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "importtrend_product_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded!");
    } catch {
      toast.error("Failed to download template");
    }
  };

  const [exporting, setExporting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleExportProducts = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/excel/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `importrend_products_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Products exported!");
    } catch {
      toast.error("Failed to export products");
    } finally {
      setExporting(false);
    }
  };

  // Sidebar "Export Products" opens this page with ?export=1
  useEffect(() => {
    if (searchParams.get("export") === "1") {
      setSearchParams({}, { replace: true });
      handleExportProducts();
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("update_existing", updateExisting ? "true" : "false");
      const res = await axios.post(`${API}/excel/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
      });

      // Read summary counts from response headers
      const created = res.headers["x-import-created"] || "0";
      const updated = res.headers["x-import-updated"] || "0";
      const skipped = res.headers["x-import-skipped"] || "0";
      const errors  = res.headers["x-import-errors"]  || "0";

      // Auto-download the result Excel
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "import_results.xlsx";
      a.click();
      URL.revokeObjectURL(url);

      toast.success(`Import done — Created: ${created} | Updated: ${updated} | Skipped: ${skipped} | Errors: ${errors}`);
      fetchProducts();
    } catch (err) {
      toast.error("Import failed. Please check the file format and try again.");
    } finally {
      setImportLoading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success("Product deleted");
      setProducts(p => p.filter(x => x.id !== id));
      setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
      setDeleteConfirm(null);
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = products.filter(p =>
    !search || p.item_name?.toLowerCase().includes(search.toLowerCase()) ||
    p.product_sku?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageRows = filtered.slice(pageStart, pageStart + pageSize);
  const allOnPageSelected = pageRows.length > 0 && pageRows.every(p => selected.has(p.id));

  useEffect(() => { setPage(1); }, [search, pageSize]);

  const toggleOne = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const togglePage = () => setSelected(prev => {
    const next = new Set(prev);
    if (allOnPageSelected) pageRows.forEach(p => next.delete(p.id));
    else pageRows.forEach(p => next.add(p.id));
    return next;
  });

  const setStatus = async (ids, status) => {
    try {
      await axios.post(`${API}/products/bulk-status`, { ids, status });
      const idSet = new Set(ids);
      setProducts(list => list.map(p => idSet.has(p.id) ? { ...p, status } : p));
      toast.success(ids.length === 1 ? `Product set to ${status}` : `${ids.length} products set to ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const runBulkDelete = async () => {
    if (!bulkConfirm) return;
    setBulkBusy(true);
    try {
      const res = await axios.post(`${API}/products/bulk-delete`, { ids: bulkConfirm.ids });
      const idSet = new Set(bulkConfirm.ids);
      setProducts(list => list.filter(p => !idSet.has(p.id)));
      setSelected(new Set());
      toast.success(`Deleted ${res.data.deleted} product(s)`);
      setBulkConfirm(null);
      setBulkTyped("");
    } catch {
      toast.error("Bulk delete failed");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="p-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Panel Products</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage your product catalog</p>
        </div>
        <button
          data-testid={ADMIN.addProductButton}
          onClick={() => navigate("/admin/products/new")}
          className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-4 py-2 rounded-lg flex items-center gap-2 text-sm shadow-sm transition-all"
        >
          <Plus className="w-4 h-4" />
          Add New Product
        </button>
      </div>

      {/* Tools bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-48 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or SKU..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10"
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid={ADMIN.downloadTemplateButton}
            onClick={handleDownloadTemplate}
            className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-orange-600 border border-slate-200 hover:border-orange-300 px-3 py-2 rounded-lg transition-all"
          >
            <Download className="w-4 h-4" />
            Template
          </button>

          <button
            data-testid="export-products-button"
            onClick={handleExportProducts}
            disabled={exporting}
            className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-orange-600 border border-slate-200 hover:border-orange-300 px-3 py-2 rounded-lg transition-all disabled:opacity-60"
          >
            <Download className="w-4 h-4" />
            {exporting ? "Exporting..." : "Export"}
          </button>

          <div className="flex items-center gap-1 border border-slate-200 rounded-lg overflow-hidden">
            <button
              data-testid={ADMIN.importExcelButton}
              onClick={() => importRef.current?.click()}
              disabled={importLoading}
              className="flex items-center gap-1.5 text-sm text-white bg-slate-700 hover:bg-slate-800 px-3 py-2 transition-all disabled:opacity-60"
            >
              {importLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Import Excel
            </button>
            <label
              data-testid="import-update-toggle"
              className={`flex items-center gap-1.5 px-3 py-2 cursor-pointer text-xs font-medium transition-all select-none ${
                updateExisting
                  ? "bg-orange-500 text-white"
                  : "bg-slate-100 text-slate-500 hover:bg-slate-200"
              }`}
              title="When enabled: updates existing products matching Product ID or SKU; otherwise always creates new"
            >
              <input
                type="checkbox"
                checked={updateExisting}
                onChange={e => setUpdateExisting(e.target.checked)}
                className="hidden"
              />
              {updateExisting ? "Update ON" : "Update OFF"}
            </label>
          </div>
          <input ref={importRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
        </div>

        <button
          onClick={fetchProducts}
          className="text-slate-400 hover:text-slate-600 p-2 rounded-lg hover:bg-slate-100 transition-all"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Bulk action bar */}
      {(selected.size > 0 || filtered.length > 0) && !loading && (
        <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 mb-3 flex flex-wrap items-center gap-2 text-sm" data-testid="bulk-bar">
          <span className="text-slate-600">{selected.size} selected</span>
          <button disabled={!selected.size} onClick={() => setBulkConfirm({ ids: [...selected], label: `${selected.size} selected product(s)`, all: false })}
            className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40" data-testid="bulk-delete-selected">Delete Selected</button>
          <button disabled={!selected.size} onClick={() => setStatus([...selected], "Live")}
            className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40">Set Live</button>
          <button disabled={!selected.size} onClick={() => setStatus([...selected], "Draft")}
            className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40">Set Draft</button>
          {selected.size > 0 && <button onClick={() => setSelected(new Set())} className="text-xs text-slate-400 hover:text-slate-600">Clear</button>}
          <button onClick={() => setBulkConfirm({ ids: filtered.map(p => p.id), label: search ? `all ${filtered.length} products matching your search` : `ALL ${filtered.length} products`, all: true })}
            className="ml-auto px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40" disabled={!filtered.length} data-testid="bulk-delete-all">
            {search ? "Delete All Matching" : "Delete All"}
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={ADMIN.productTable}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <Package className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-slate-700 font-semibold mb-1">No products found</h3>
            <p className="text-slate-400 text-sm mb-4">
              {search ? "Try a different search" : "Start by adding a product or importing from Excel"}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => navigate("/admin/products/new")}
                className="bg-orange-500 text-white text-sm px-4 py-2 rounded-lg hover:bg-orange-600 transition-all"
              >
                Add Product
              </button>
              <button
                onClick={handleDownloadTemplate}
                className="border border-slate-300 text-slate-600 text-sm px-4 py-2 rounded-lg hover:bg-slate-50 transition-all"
              >
                Download Template
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3 w-10">
                    <input type="checkbox" checked={allOnPageSelected} onChange={togglePage} className="w-4 h-4 accent-orange-500 cursor-pointer" data-testid="select-all-page" aria-label="Select all on this page" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-12">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Product ID / SKU</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">MRP</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pageRows.map((product, idx) => (
                  <tr
                    key={product.id}
                    data-testid={ADMIN.productRow}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={selected.has(product.id)} onChange={() => toggleOne(product.id)} className="w-4 h-4 accent-orange-500 cursor-pointer" aria-label={`Select ${product.item_name || "product"}`} data-testid="select-row" />
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{pageStart + idx + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg border border-slate-200 overflow-hidden flex-shrink-0 bg-slate-50">
                          {product.main_image ? (
                            <img
                              src={product.main_image}
                              alt={product.item_name}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = "none"; }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-4 h-4 text-slate-300" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 truncate max-w-48">{product.item_name || "—"}</p>
                          <p className="text-xs text-slate-400 truncate">
                            {product.collection?.join(", ") || "No collection"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        {product.product_id && (
                          <span className="text-xs text-slate-400">ID: {product.product_id}</span>
                        )}
                        <span className="font-mono text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                          {product.product_sku || "—"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{product.product_type || "—"}</td>
                    <td className="px-4 py-3 text-slate-700 font-medium">
                      {product.mrp ? `₹${product.mrp}` : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <button type="button" title={product.status === "Live" ? "Click to set Draft" : "Click to set Live"} data-testid="status-toggle"
                        onClick={() => setStatus([product.id], product.status === "Live" ? "Draft" : "Live")}
                        className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full cursor-pointer hover:opacity-80 ${
                        product.status === "Live"
                          ? "bg-green-100 text-green-700"
                          : "bg-amber-100 text-amber-700"
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          product.status === "Live" ? "bg-green-500" : "bg-amber-500"
                        }`} />
                        {product.status || "Draft"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => window.open(`/products/${product.id}`, "_blank")}
                          className="p-1.5 text-slate-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-all"
                          title="Preview product page"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={ADMIN.editProductButton}
                          onClick={() => navigate(`/admin/products/${product.id}`)}
                          className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-all"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={ADMIN.deleteProductButton}
                          onClick={() => setDeleteConfirm(product)}
                          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
        <span>Showing {filtered.length ? pageStart + 1 : 0}-{Math.min(pageStart + pageSize, filtered.length)} of {filtered.length} products{filtered.length !== products.length ? ` (${products.length} total)` : ""}</span>
        <div className="flex items-center gap-2" data-testid="pagination">
          <label className="flex items-center gap-1">Per page
            <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} className="border border-slate-200 rounded px-1.5 py-1 bg-white">
              {[25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)} className="p-1.5 border border-slate-200 rounded disabled:opacity-40" aria-label="Previous page"><ChevronLeft className="w-4 h-4" /></button>
          <span>Page {currentPage} of {totalPages}</span>
          <button disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)} className="p-1.5 border border-slate-200 rounded disabled:opacity-40" aria-label="Next page"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Bulk delete confirm */}
      {bulkConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center"><AlertCircle className="w-5 h-5 text-red-600" /></div>
              <div><h3 className="font-semibold text-slate-900">Delete products</h3><p className="text-sm text-slate-500">This action cannot be undone</p></div>
            </div>
            <p className="text-sm text-slate-600 mb-3">You are about to delete <strong>{bulkConfirm.label}</strong>.</p>
            {bulkConfirm.all && (
              <input value={bulkTyped} onChange={e => setBulkTyped(e.target.value)} placeholder="Type DELETE to confirm" className="w-full mb-4 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-red-500" data-testid="bulk-confirm-input" />
            )}
            <div className="flex gap-3">
              <button onClick={() => { setBulkConfirm(null); setBulkTyped(""); }} className="flex-1 border border-slate-300 text-slate-700 py-2 rounded-lg text-sm font-medium hover:bg-slate-50">Cancel</button>
              <button disabled={bulkBusy || (bulkConfirm.all && bulkTyped !== "DELETE")} onClick={runBulkDelete} className="flex-1 bg-red-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-40" data-testid="bulk-confirm-delete">{bulkBusy ? "Deleting..." : "Delete"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl animate-fadeIn">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Delete Product</h3>
                <p className="text-sm text-slate-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-5">
              Are you sure you want to delete <strong>"{deleteConfirm.item_name}"</strong>?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 border border-slate-300 text-slate-700 py-2 rounded-lg text-sm font-medium hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-all"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
